import React, { useEffect } from 'react';
import { 
  BarChart, Bar, PieChart, Pie,
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell
} from 'recharts';
import {
  X, Briefcase, Building, MapPin, TrendingUp,
  Wallet, Book, Clock, Target, GraduationCap
} from 'lucide-react';

// Enhanced semantic color schemes
const CHART_COLORS = {
  // Skills palette - blues for professional skills
  skills: {
    colors: [
      '#0C4A6E',  // Darkest blue for most in-demand
      '#0369A1',
      '#0EA5E9',
      '#38BDF8',
      '#7DD3FC',
      '#BAE6FD',  // Lightest for least in-demand
    ],
    background: '#F0F9FF',
    title: 'Skills Distribution'
  },
  // Locations palette - greens for geographical spread
  locations: {
    colors: [
      '#166534',  // Darkest green for highest concentration
      '#15803D',
      '#16A34A',
      '#22C55E',
      '#4ADE80',
      '#86EFAC',  // Lightest for lowest concentration
    ],
    background: '#ECFDF5',
    title: 'Location Distribution'
  },
  // Job Types palette - purples for role categories
  jobTypes: {
    colors: [
      '#581C87',  // Darkest purple for most common
      '#7E22CE',
      '#9333EA',
      '#A855F7',
      '#C084FC',
      '#E9D5FF',  // Lightest for least common
    ],
    background: '#F5F3FF',
    title: 'Job Types'
  }
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100">
      <p className="font-medium text-gray-900">{label}</p>
      {payload.map((item, index) => (
        <p key={index} className="text-sm text-gray-600">
          Count: {item.value}
        </p>
      ))}
    </div>
  );
};

const formatCurrency = (amount) => {
  if (!amount) return '₹0';
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${amount}`;
};

const ChartContainer = ({ title, children, colorScheme, icon: Icon }) => (
  <div className={`bg-white rounded-xl p-6 shadow-sm border border-gray-100 ${colorScheme?.background}`}>
    <div className="flex items-center space-x-2 mb-6">
      <Icon className="w-5 h-5 text-gray-600" />
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    </div>
    <div className="h-80">
      {children}
    </div>
  </div>
);

const SkillsChart = ({ data }) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data} layout="vertical">
      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
      <XAxis type="number" />
      <YAxis 
        dataKey="skill" 
        type="category" 
        width={120}
        tick={{ fill: '#374151', fontSize: 12 }}
      />
      <Tooltip content={<CustomTooltip />} />
      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
        {data.map((_, index) => (
          <Cell 
            key={`cell-${index}`} 
            fill={CHART_COLORS.skills.colors[Math.min(index, CHART_COLORS.skills.colors.length - 1)]}
          />
        ))}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const LocationsChart = ({ data }) => (
  <ResponsiveContainer width="100%" height="100%">
    <PieChart>
      <Pie
        data={data}
        dataKey="count"
        nameKey="location"
        cx="50%"
        cy="50%"
        innerRadius={60}
        outerRadius={80}
        paddingAngle={5}
      >
        {data.map((_, index) => (
          <Cell 
            key={`cell-${index}`} 
            fill={CHART_COLORS.locations.colors[Math.min(index, CHART_COLORS.locations.colors.length - 1)]}
          />
        ))}
      </Pie>
      <Tooltip content={<CustomTooltip />} />
      <Legend verticalAlign="middle" align="right" layout="vertical" />
    </PieChart>
  </ResponsiveContainer>
);

const JobTypesChart = ({ data }) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data}>
      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
      <XAxis dataKey="type" />
      <YAxis />
      <Tooltip content={<CustomTooltip />} />
      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
        {data.map((_, index) => (
          <Cell 
            key={`cell-${index}`} 
            fill={CHART_COLORS.jobTypes.colors[Math.min(index, CHART_COLORS.jobTypes.colors.length - 1)]}
          />
        ))}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const MetricCard = ({ title, value, icon: Icon, subtitle, colorClass = "bg-blue-50" }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
    <div className="flex justify-between items-start">
      <div className="space-y-2">
        <p className="text-sm text-gray-600">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-lg ${colorClass}`}>
        <Icon className={`h-6 w-6 ${colorClass.replace('bg-', 'text-').replace('50', '600')}`} />
      </div>
    </div>
  </div>
);

const JobAnalyticsModal = ({ isOpen, onClose, jobs }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !jobs?.length) return null;

  const processAnalytics = () => {
    // Location distribution
    const locationData = jobs.reduce((acc, job) => {
      const location = job.company.location;
      acc[location] = (acc[location] || 0) + 1;
      return acc;
    }, {});

    // Skills demand analysis
    const skillsData = jobs.reduce((acc, job) => {
      job.jobDetails.skills.forEach(skill => {
        acc[skill] = (acc[skill] || 0) + 1;
      });
      return acc;
    }, {});

    // Average salary calculation
    const averageSalary = jobs.reduce((acc, job) => {
      const avg = (job.jobDetails.salary.min + job.jobDetails.salary.max) / 2;
      return acc + avg;
    }, 0) / jobs.length;

    // Job types distribution
    const jobTypesData = jobs.reduce((acc, job) => {
      const type = job.jobDetails.type;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    return {
      locations: Object.entries(locationData)
        .map(([location, count]) => ({ location, count }))
        .sort((a, b) => b.count - a.count),
      skills: Object.entries(skillsData)
        .map(([skill, count]) => ({ skill, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8),
      jobTypes: Object.entries(jobTypesData)
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count),
      stats: {
        totalJobs: jobs.length,
        averageSalary,
        totalCompanies: new Set(jobs.map(job => job.company.name)).size,
        referralJobs: jobs.filter(job => job.referralEnabled).length
      }
    };
  };

  const analytics = processAnalytics();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className="relative w-full max-w-7xl bg-gray-50 rounded-2xl shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-6 py-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Job Market Insights</h2>
                <p className="text-sm text-gray-500">Current trends and opportunities analysis</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                title="Active Opportunities"
                value={analytics.stats.totalJobs}
                icon={Briefcase}
                colorClass="bg-blue-50"
              />
              <MetricCard
                title="Average Package"
                value={formatCurrency(analytics.stats.averageSalary)}
                icon={Wallet}
                subtitle="Per annum"
                colorClass="bg-green-50"
              />
              <MetricCard
                title="Companies Hiring"
                value={analytics.stats.totalCompanies}
                icon={Building}
                colorClass="bg-purple-50"
              />
              <MetricCard
                title="Referral Jobs"
                value={analytics.stats.referralJobs}
                icon={Target}
                subtitle={`${Math.round((analytics.stats.referralJobs / analytics.stats.totalJobs) * 100)}% of total`}
                colorClass="bg-orange-50"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartContainer 
                title="Most In-demand Skills" 
                colorScheme={CHART_COLORS.skills}
                icon={Book}
              >
                <SkillsChart data={analytics.skills} />
              </ChartContainer>

              <ChartContainer 
                title="Job Locations" 
                colorScheme={CHART_COLORS.locations}
                icon={MapPin}
              >
                <LocationsChart data={analytics.locations} />
              </ChartContainer>
            </div>

            <ChartContainer 
              title="Job Types Distribution" 
              colorScheme={CHART_COLORS.jobTypes}
              icon={Briefcase}
            >
              <JobTypesChart data={analytics.jobTypes} />
            </ChartContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobAnalyticsModal;